import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { CloseIcon } from "../assets/icons";
import { Shortcut } from "../@types";
import { defaultShortcuts } from "../constants/helpServices";

type RangeGenerator = () => [Date, Date];
interface DatePickerWithShortcutsProps {
  shortcuts?: Shortcut[];
  onRangeSelect: (range: [Date, Date]) => void;
  selected: [Date, Date] | null;
  onClose: () => void;
  onApplyButtonClick: (startDate: Date, endDate: Date) => Promise<void>;
  fetchData?: () => void;
  startDate?: Date | null;
  endDate?: Date | null;
  close?: boolean;
}

const DatePickerWithShortcuts: React.FC<DatePickerWithShortcutsProps> = ({
  shortcuts = defaultShortcuts,
  onRangeSelect,
  selected,
  onClose,
  onApplyButtonClick,
  fetchData,
  startDate: start,
  endDate: end,
  close,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(start ?? new Date());
  const [endDate, setEndDate] = useState<Date | null>(end ?? new Date());
  const [, setSelected] = useState<[Date, Date] | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [applyActiveIndex, setApplyActiveIndex] = useState<number | null>(null);

  const handleClose = () => {
    setIsOpen(false);
    setShowCalendar(!showCalendar);
    onClose();
  };

  const handleClearFilter = () => {
    setIsOpen(false);
    setShowCalendar(!showCalendar);
    onClose();
    fetchData && fetchData();
  };

  useEffect(() => {
    if (selected && !mounted) {
      setMounted(true);
      setIsOpen(true);
    }
  }, [selected, mounted]);

  const handleShortcutClick = (
    rangeGenerator: RangeGenerator,
    index: number,
  ): void => {
    const range: [Date, Date] = rangeGenerator();
    setSelected(range);
    setStartDate(range[0]);
    setEndDate(range[1]);
    setActiveIndex(index);
  };

  const handleCalendarChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setIsCustomRange(true);
  };

  const handleApplyButtonClick = async () => {
    if (startDate && endDate) {
      await onApplyButtonClick(startDate, endDate);
    } else if (startDate && !endDate) {
      //@ts-ignore
      await onApplyButtonClick(startDate, null);
    }
    setApplyActiveIndex(activeIndex);
  };

  useEffect(() => {
    if (selected) {
      setStartDate(selected[0]);
      setEndDate(selected[1]);
    }
  }, [selected]);

  if (!isOpen) {
    return null;
  }

  return (
    //Mobile view
    <>
      <div className=" relative">
        <div className="md:hidden bg-white rounded-md w-[full] h-[587px]  fixed bottom-0 right-0 left-0">
          <div className="flex justify-between mb-4">
            <p className="text-[16px] font-medium pl-[1em] pt-[1em] mt-[0.5em]">
              Pick a date range:
            </p>
            <div
              className="lg:pr-[2em] lg:pt-[1em] pt-[1em] pr-[0.5em] md:mr-[1em] cursor-pointer biglg:mr-[0.5em]"
              onClick={handleClose}
            >
              <CloseIcon />
            </div>
          </div>
          <hr className="w-[100%] text-[#A4A4A4] lg:mt-[-0.2em]" />

          <div>
            <div className="w-full  mx-auto px-[1em] h-[334px] mt-[3em] ">
              <DatePicker
                selected={startDate}
                onChange={handleCalendarChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                inline
                className="text-[12px]  w-full ml-[4em] !important "
                wrapperClassName="w-full h-[344px] "
                calendarClassName="w-full h-[344px] !important"
                popperClassName="w-full h-[334px] "
              />
            </div>
          </div>
          <div className="flex justify-end items-center mt-[2.5em]  pr-[1em] pb-0 ">
            <button
              onClick={handleClose}
              className="lg:bg-white bg-[#FFFFFF] md:bg-[#FFFFFF] border border-[#044E97] text-[#044E97] py-2 px-4 rounded ml-2 w-[156px] h-[48px] text-[16px] mr-[1em]"
            >
              {close ? "Close Filter" : "Clear Filter"}
            </button>
            <button
              onClick={handleApplyButtonClick}
              className=" bg-[#044E97] md:bg-[#044E97] text-white py-2 px-4 rounded ml-2 w-[71px] h-[48px] text-[16px] xl:mr-[1em] md:mr-[1.7em]"
            >
              OK
            </button>
          </div>
        </div>
      </div>

      {/* Desktop and Tablet */}
      <div className="bg-white rounded-md mt-[4em] w-full h-[617px] md:w-[400px] md:h-[505px] lg:w-[400px] lg:h-[502px] xl:h-[510px] biglg:h-[490px] hidden md:block">
        <div className="flex justify-between mb-4">
          <p className="lg:pl-[2em] lg:pt-[0.3em] lg:text-[16px] md:pl-[2em] md:pt-[1em] md:text-[16px] md:font-semibold text-[12px] pl-[1em] pt-[1em] mt-[0.5em]">
            Pick a date range:
          </p>
          <div
            className="lg:pr-[2em] lg:pt-[1em] pt-[1em] pr-[0.5em] md:mr-[1em] cursor-pointer biglg:mr-[0.5em]"
            onClick={handleClose}
          >
            <CloseIcon />
          </div>
        </div>
        <hr className="w-[100%] text-[#A4A4A4] lg:mt-[-0.2em]" />

        <div className="flex px-[5px] justify-center items-center font-normal">
          <div className="w-[358px] mx-auto h-[334px] mt-[1em] md:mt-[1em]  md:justify-end rounded-md">
            <DatePicker
              selected={startDate}
              onChange={handleCalendarChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
              className="text-[12px]  w-[358px] mr-[1em] !important "
              wrapperClassName="w-[358px] h-[344px] "
              calendarClassName="w-[358px] h-[344px] md:mr-[1em] !important"
              popperClassName="w-[358px] h-[334px] "
            />
          </div>
        </div>
        <hr className="w-[100%] text-[#A4A4A4] md:mt-[2em] biglg:mt-[1.5em]" />
        <div className="flex justify-end items-center mt-[0.5em] pr-[1em] md:pb-0">
          <button
            onClick={handleClearFilter}
            className="lg:bg-white bg-[#FFFFFF] md:bg-[#FFFFFF] border border-[#044E97] text-[#044E97] py-2 px-4 rounded ml-2 w-[156px] h-[48px] text-[16px] mr-[1em]"
          >
            {close ? "Close Filter" : "Clear Filter"}
          </button>
          <button
            onClick={handleApplyButtonClick}
            className=" bg-[#044E97] md:bg-[#044E97] text-white py-2 px-4 rounded ml-2 w-[71px] h-[48px] text-[16px] xl:mr-[1em] md:mr-[1.7em]"
          >
            OK
          </button>
        </div>
      </div>
    </>
  );
};

export default DatePickerWithShortcuts;
